import React, { Fragment } from 'react';

import { RendererActionsContext } from '@atlaskit/renderer/actions';
import type { ExtensionHandler } from '@atlaskit/editor-common/extensions';

import { createMacroExtensionHandler } from '@confluence/fabric-extension-lib/entry-points/extensions-common';
import { EXTENSION_KEY } from '@confluence/fabric-extension-lib/entry-points/extensionConstants';
import {
	getExperienceName,
	getMacroAttributesFromADFNode,
	MacroExperienceStart,
	RENDERER,
} from '@confluence/macro-tracker';
import { ExperienceTimeout } from '@confluence/experience-tracker';

import type {
	InlineExternalImageParams,
	InlineImageParams,
	ExtensionParamsType,
} from '../extensionHandlerTypes';
import { InvalidExtensionLoader as InvalidExtension } from '../InvalidExtension/InvalidExtensionLoader';
import { InlineExternalImage } from '../InlineExternalImage';
import { InlineImage } from '../InlineImage';

export const getMigrationExtHandlerForRenderer = ({
	contentId,
	contextProviders,
}): ExtensionHandler<ExtensionParamsType> =>
	createMacroExtensionHandler((extension) => {
		if (!extension) {
			return null;
		}

		const { extensionKey, parameters } = extension;
		const { ClassicEditorContextProvider = ({ children }) => children } = contextProviders;
		const attributes = getMacroAttributesFromADFNode(extension);
		const defaultProps = {
			contentId,
			mode: RENDERER,
			extensionKey,
			attributes,
			node: extension,
		};

		let implementation;

		switch (extensionKey) {
			case EXTENSION_KEY.INVALID:
				// extension is rendered outside of React DOM tree and require access to the ClassicEditorContext
				implementation = (
					<ClassicEditorContextProvider>
						<InvalidExtension parameters={parameters} {...defaultProps} />
					</ClassicEditorContextProvider>
				);
				break;

			case EXTENSION_KEY.INLINE_EXTERNAL_IMAGE:
				const inlineExternalImageProps = parameters as InlineExternalImageParams;
				implementation = <InlineExternalImage {...inlineExternalImageProps} {...defaultProps} />;
				break;

			case EXTENSION_KEY.INLINE_MEDIA_IMAGE:
				const inlineMediaImageProps = parameters as InlineImageParams;
				const { link } = parameters;
				implementation = <InlineImage {...inlineMediaImageProps} {...defaultProps} link={link} />;
				break;
		}

		return (
			<Fragment>
				<MacroExperienceStart
					name={getExperienceName(RENDERER, extension)}
					mode={RENDERER}
					extensionKey={extensionKey}
					attributes={attributes}
					timeout={ExperienceTimeout.MACRO_LOAD}
					contentId={contentId}
				/>
				<RendererActionsContext>{implementation}</RendererActionsContext>
			</Fragment>
		);
	});
